import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight, ExternalLink, X } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { cx } from '../../lib/classNames';
import { lockPageScroll } from '../../lib/scrollLock';
import {
  dismissAnnouncement,
  rememberAnnouncementVisit,
  restoreAnnouncement,
} from '../../lib/announcement';
import { ROUTES } from '../../routes';
import { QLD_2027_REQUIREMENTS_URL } from '../../content/site';
import PortalSelector from '../PortalSelector';
import styles from './Topbar.module.css';

import myloLogo from '../../assets/images/logo_yellow.svg';


const NAV_ITEMS = [
  { label: 'Home', route: ROUTES.home },
  { label: 'Safety Compliance', route: ROUTES.safety },
  { label: 'Tradie Services', route: ROUTES.tradies },
  { label: 'Home Modifications', route: ROUTES.homeModifications },
  { label: 'Energy Rebates', route: ROUTES.energyRebates },
  { label: 'Careers', route: ROUTES.careers },
  { label: 'Contact', route: ROUTES.contact },
];

const FOCUSABLE_ELEMENTS = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function ExternalLinkIcon() {
  return <i aria-hidden="true"><ExternalLink size="1em" strokeWidth={2.8} /></i>;
}

function NavItems({ mobile = false, onNavigate }) {
  return NAV_ITEMS.map(({ label, route }) => (
    <NavLink
      key={route}
      className={({ isActive }) => cx(
        mobile ? styles.mobile_nav_link : styles.nav_link,
        isActive && (mobile ? styles.mobile_nav_link_active : styles.nav_link_active),
      )}
      to={route}
      end={route === ROUTES.home}
      onClick={onNavigate}
    >
      <span>{label}</span>
      {mobile && <i aria-hidden="true"><ArrowRight size="1em" strokeWidth={2.8} /></i>}
    </NavLink>
  ));
}

function Topbar() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const brandRef = useRef(null);
  const menuRef = useRef(null);
  const closeButtonRef = useRef(null);

  useClientLayoutEffect(() => {
    // Also initialise CSR entry points; the inline bootstrap handles the first
    // paint of pregenerated HTML before the React bundle has loaded.
    restoreAnnouncement();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') rememberAnnouncementVisit();
      else restoreAnnouncement();
    };
    const handlePageHide = () => {
      // A background tab may already have been away for a long time. Do not
      // renew its old dismissal if that tab is subsequently reloaded/closed.
      if (document.visibilityState !== 'hidden') rememberAnnouncementVisit();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', restoreAnnouncement);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', restoreAnnouncement);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    rememberAnnouncementVisit();
  }, [pathname]);

  useEffect(() => {
    // Keep in sync with the mobile breakpoint in Topbar.module.css.
    const mobileMedia = window.matchMedia('(max-width: 1040px)');
    const closeOnDesktop = ({ matches }) => {
      if (!matches) setMenuOpen(false);
    };

    mobileMedia.addEventListener('change', closeOnDesktop);
    return () => mobileMedia.removeEventListener('change', closeOnDesktop);
  }, []);

  useClientLayoutEffect(() => {
    if (!menuOpen) return undefined;

    const openingPath = window.location.pathname;
    const previouslyFocused = document.activeElement;
    const unlockScroll = lockPageScroll();

    const focusCloseButton = window.requestAnimationFrame(() => closeButtonRef.current?.focus({ preventScroll: true }));

    const handleKeyDown = (event) => {
      if (!menuRef.current?.contains(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(menuRef.current?.querySelectorAll(FOCUSABLE_ELEMENTS) || []);
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusCloseButton);
      document.removeEventListener('keydown', handleKeyDown);

      unlockScroll();
      if (window.location.pathname === openingPath && previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [menuOpen]);

  return (
    <>
      <aside className={styles.announcement} aria-label="MYLO service announcement">
        <div className={styles.announcement_inner}>
          <p className={styles.announcement_copy}>
            <span className={styles.announcement_message}>
              {'MYLO is now in '}
              <Link className={styles.announcement_state_link} to={ROUTES.safetyQueensland}>Queensland</Link>
              {' and '}
              <Link className={styles.announcement_state_link} to={ROUTES.safetySouthAustralia}>South Australia</Link>
            </span>
            <a
              className={styles.announcement_link}
              href={QLD_2027_REQUIREMENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              QLD 2027 requirements
              <ExternalLink size={14} aria-hidden="true" focusable="false" />
            </a>
          </p>
          <button
            className={styles.announcement_close}
            type="button"
            aria-label="Dismiss service announcement"
            onClick={() => {
              dismissAnnouncement();
              brandRef.current?.focus({ preventScroll: true });
            }}
          >
            <X size={18} strokeWidth={2} aria-hidden="true" focusable="false" />
          </button>
        </div>
      </aside>
      <header className={styles.header}>
        <div className={styles.inner}>
          <Link ref={brandRef} className={styles.brand} to={ROUTES.home} aria-label="MYLO home">
            <img className={styles.logo} src={myloLogo} width="127" height="119" alt="MYLO" />
            <span className={styles.brand_tagline}>
              <span>Property Solutions,</span>
              <span>Simplified.</span>
            </span>
          </Link>

          <nav className={styles.nav} aria-label="Primary navigation">
            <NavItems />
          </nav>

          <div className={styles.actions}>
            <PortalSelector
              className={cx(styles.action, styles.portal_action)}
            >
              <span>MYLO Portal</span>
              <ExternalLinkIcon />
            </PortalSelector>
          </div>

          <button
            className={styles.menu_button}
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen(true)}
          >
            <span className={styles.menu_icon} aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>

        {menuOpen && (
          <div
            ref={menuRef}
            id="mobile-navigation"
            className={styles.mobile_menu}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className={styles.mobile_menu_inner}>
              <div className={styles.mobile_menu_header}>
                <Link className={styles.mobile_brand} to={ROUTES.home} aria-label="MYLO home" onClick={() => setMenuOpen(false)}>
                  <img className={styles.mobile_logo} src={myloLogo} width="127" height="119" alt="MYLO" />
                </Link>

                <button
                  ref={closeButtonRef}
                  className={styles.close_button}
                  type="button"
                  aria-label="Close navigation menu"
                  onClick={() => setMenuOpen(false)}
                >
                  <span aria-hidden="true" />
                  <span aria-hidden="true" />
                </button>
              </div>

              <div className={styles.mobile_menu_content}>
                <div className={styles.mobile_actions}>
                  <PortalSelector
                    className={cx(styles.mobile_action, styles.mobile_portal_action)}
                    onSelect={() => setMenuOpen(false)}
                  >
                    <span>MYLO Portal</span>
                    <ExternalLinkIcon />
                  </PortalSelector>
                </div>

                <nav className={styles.mobile_nav} aria-label="Mobile primary navigation">
                  <NavItems mobile onNavigate={() => setMenuOpen(false)} />
                </nav>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

export default Topbar;
